/**
 * User Management API Service
 * Handles all user management operations including CRUD operations
 */

import { apiClient } from './apiClient';

export interface User {
  id: string;
  email?: string;
  phone: string;
  title?: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'host' | 'guest' | 'usher';
  roles?: string[]; // Array of all roles user has
  is_active: boolean;
  is_verified: boolean;
  is_staff: boolean;
  preferred_language: string;
  created_at: string;
  updated_at: string;
  last_login?: string;
}

export interface UserCreateData {
  email?: string;
  phone: string;
  title?: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'host' | 'guest' | 'usher';
  roles?: string[]; // Array of roles (for multi-role support)
  is_active?: boolean;
  is_verified?: boolean;
  password: string;
  password_confirm: string;
}

export interface UserUpdateData {
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  role?: 'admin' | 'host' | 'guest' | 'usher';
  roles?: string[]; // Array of roles (for multi-role support)
  is_active?: boolean;
  is_verified?: boolean;
  password?: string;
  password_confirm?: string;
}

export interface UserListParams {
  page?: number;
  page_size?: number;
  role?: 'admin' | 'host' | 'guest' | 'usher'; // Deprecated: use roles instead
  roles?: string[]; // Array of roles to filter by (supports multiple roles)
  is_active?: boolean;
  is_verified?: boolean;
  search?: string;
  ordering?: string;
}

export interface UserListResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      page: number;
      page_size: number;
      total_count: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  };
}

export interface UserResponse {
  success: boolean;
  data: User;
  message?: string;
  requires_confirmation?: boolean;
  deleted_user?: {
    id: string;
    name: string;
    email?: string;
    phone: string;
    role: string;
    deleted_at?: string;
    deleted_date?: string;
  };
  restored?: boolean;
  error?: {
    type: string;
    message: string;
    details: any;
  };
}

export interface UserStatistics {
  success: boolean;
  data: {
    total_users: number;
    active_users: number;
    inactive_users: number;
    admin_users: number;
    host_users: number;
    guest_users: number;
    usher_users: number;
    verified_users: number;
    unverified_users: number;
  };
}

export interface ApiError {
  success: false;
  error: {
    type: string;
    message: string;
    details: any;
  };
}

class UserManagementService {
  private baseUrl = '/users';

  /**
   * Get list of users with filtering and pagination
   */
  async getUsers(params: UserListParams = {}): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.page_size) queryParams.append('page_size', params.page_size.toString());
    // Support multiple roles filtering
    if (params.roles && params.roles.length > 0) {
      queryParams.append('roles', params.roles.join(','));
    } else if (params.role) {
      // Fallback to single role for backward compatibility
      queryParams.append('role', params.role);
    }
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params.is_verified !== undefined) queryParams.append('is_verified', params.is_verified.toString());
        if (params.search) {
          queryParams.append('search', params.search);
        }
    if (params.ordering) queryParams.append('ordering', params.ordering);

    const url = `${this.baseUrl}/?${queryParams.toString()}`;
    
    const response = await apiClient.get(url, { cache: false });
    
    return response.data;
  }

  /**
   * Get a specific user by ID
   */
  async getUser(userId: string): Promise<UserResponse> {
    const response = await apiClient.get(`${this.baseUrl}/${userId}/`);
    return response.data;
  }

  /**
   * Create a new user
   * @param userData User data to create
   * @param restoreOnCreate If true, will restore a deleted user if one exists with the same email/phone
   */
  async createUser(userData: UserCreateData, restoreOnCreate: boolean = false): Promise<UserResponse> {
    const data = { ...userData, restore_on_create: restoreOnCreate };
    const response = await apiClient.post(`${this.baseUrl}/`, data);
    return response.data;
  }

  /**
   * Update a user (full update)
   */
  async updateUser(userId: string, userData: UserUpdateData): Promise<UserResponse> {
    const response = await apiClient.put(`${this.baseUrl}/${userId}/`, userData);
    return response.data;
  }

  /**
   * Partially update a user
   */
  async partialUpdateUser(userId: string, userData: Partial<UserUpdateData>): Promise<UserResponse> {
    const response = await apiClient.patch(`${this.baseUrl}/${userId}/`, userData);
    return response.data;
  }

  /**
   * Delete a user (hard delete - permanently removes the user)
   */
  async deleteUser(userId: string): Promise<{ success: boolean; message: string; error?: { type: string; message: string; details: any } }> {
    const response = await apiClient.delete(`${this.baseUrl}/${userId}/`);
    return response.data;
  }

  /**
   * Activate a user
   */
  async activateUser(userId: string): Promise<UserResponse> {
    const response = await apiClient.post(`${this.baseUrl}/${userId}/activate/`);
    return response.data;
  }

  /**
   * Restore a soft-deleted user
   */
  async restoreUser(userId: string): Promise<UserResponse> {
    const response = await apiClient.post(`${this.baseUrl}/${userId}/restore/`);
    return response.data;
  }

  /**
   * Deactivate a user
   */
  async deactivateUser(userId: string): Promise<UserResponse> {
    const response = await apiClient.post(`${this.baseUrl}/${userId}/deactivate/`);
    return response.data;
  }

  /**
   * Get user statistics
   */
  async getUserStatistics(): Promise<UserStatistics> {
    const response = await apiClient.get(`${this.baseUrl}/statistics/`);
    return response.data;
  }

  /**
   * Search users by name, email, or phone
   */
  async searchUsers(searchTerm: string, params: Omit<UserListParams, 'search'> = {}): Promise<UserListResponse> {
    return this.getUsers({ ...params, search: searchTerm });
  }

  /**
   * Get users by role
   */
  async getUsersByRole(role: 'admin' | 'host' | 'guest' | 'usher', params: Omit<UserListParams, 'role'> = {}): Promise<UserListResponse> {
    return this.getUsers({ ...params, role });
  }

  /**
   * Get active users only
   */
  async getActiveUsers(params: Omit<UserListParams, 'is_active'> = {}): Promise<UserListResponse> {
    return this.getUsers({ ...params, is_active: true });
  }

  /**
   * Get inactive users only
   */
  async getInactiveUsers(params: Omit<UserListParams, 'is_active'> = {}): Promise<UserListResponse> {
    return this.getUsers({ ...params, is_active: false });
  }

  /**
   * Bulk upload users from Excel file
   */
  async bulkUploadUsers(file: File): Promise<BulkUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post(`${this.baseUrl}/bulk_upload/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Import historical events from Excel file with multiple sheets
   */
  async importHistoricalEvents(file: File, skipDuplicates: boolean = true): Promise<HistoricalEventsImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('skip_duplicates', skipDuplicates.toString());
    
    const response = await apiClient.post(`${this.baseUrl}/historical-events/import/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Retry importing a single row with fixed data
   */
  async retryHistoricalEventRow(guestData: any, importBatchId: string, filename: string): Promise<any> {
    const response = await apiClient.post(`${this.baseUrl}/historical-events/retry-row/`, {
      guest_data: guestData,
      import_batch_id: importBatchId,
      filename: filename
    });
    return response.data;
  }
}

export interface BulkUploadResponse {
  success: boolean;
  message: string;
  data: {
    total_processed: number;
    created_count: number;
    failed_count: number;
    role_breakdown: {
      admin: number;
      host: number;
      guest: number;
    };
    created_users: Array<{
      id: string;
      name: string;
      phone: string;
      email?: string;
      role: string;
    }>;
    failed_users: Array<{
      row: number;
      name: string;
      reason: string;
    }>;
  };
  error?: {
    type: string;
    message: string;
    details: any;
  };
}

export interface HistoricalEventsImportResponse {
  success: boolean;
  message?: string;
  data: {
    total_processed: number;
    successful: number;
    failed: number;
    duplicates_skipped: number;
    skipped_duplicates?: Array<{
      sheet: string;
      row: number;
      guest_name: string;
      phone: string;
      existing_guest_name: string;
      existing_guest_phone: string;
    }>;
    errors: Array<{
      sheet: string;
      row: number;
      error: string;
      guest_name: string;
      data: any;
    }>;
    import_batch_id: string;
  };
  error?: {
    type: string;
    message: string;
    details: any;
  };
}

export const userManagementService = new UserManagementService();
export default userManagementService;
